import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { documentWorkflowControllerBulkClaimGenerationStatusOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import type { BulkClaimGenerationJobStatusRes } from '@dealstack/shared/lib/backendClient';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { isBulkClaimGenerationJobTerminal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkClaimOnBehalf/BulkClaimOnBehalf.utils';

const POLL_INTERVAL_MS = 2000;

export const useBulkClaimGenerationStatus = ({
	dealId,
	programmeId,
	jobId,
	onTerminal,
}: {
	dealId: string;
	programmeId: string;
	jobId: string | null;
	onTerminal?: (state: BulkClaimGenerationJobStatusRes) => void;
}) => {
	const queryClient = useQueryClient();
	const documentListKey = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET,
		{ dealId, programmeId }
	);

	const q = useQuery({
		...documentWorkflowControllerBulkClaimGenerationStatusOptions({
			path: { dealId, programmeId, jobId: jobId ?? '' },
		}),
		enabled: !!jobId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (!data) {
				return POLL_INTERVAL_MS;
			}
			if (isBulkClaimGenerationJobTerminal(data.status)) {
				return false;
			}
			return POLL_INTERVAL_MS;
		},
	});

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		void (async () => {
			const data = q.data;
			if (isBulkClaimGenerationJobTerminal(data.status)) {
				await queryClient.invalidateQueries({ queryKey: [documentListKey] });
				onTerminal?.(data);
			}
		})();
	}, [q.isSuccess, q.data]);

	return q;
};
